import { useState, useEffect } from "react";
import { Save, Globe, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    custom_domain: '',
    price_match_percentage: 12.0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings({
        custom_domain: response.data.custom_domain || '',
        price_match_percentage: response.data.price_match_percentage || 12.0
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'number' ? parseFloat(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.patch(`${API}/settings`, settings);
      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-settings">
        <h1 className="text-3xl font-heading font-bold text-primary mb-8">
          Settings
        </h1>

        <form onSubmit={handleSubmit} className="max-w-3xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Custom Domain
              </CardTitle>
              <CardDescription>
                Configure your custom domain for the booking website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="custom_domain">Domain Name</Label>
                <Input
                  id="custom_domain"
                  name="custom_domain"
                  type="text"
                  value={settings.custom_domain}
                  onChange={handleInputChange}
                  placeholder="e.g., book.kspaceproperties.com"
                  data-testid="custom-domain-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the domain you'd like to use. You'll need to configure DNS records to point to this service.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5" />
                Price Match Settings
              </CardTitle>
              <CardDescription>
                Configure the price comparison shown to guests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="price_match_percentage">Discount Percentage vs Airbnb</Label>
                <Input
                  id="price_match_percentage"
                  name="price_match_percentage"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={settings.price_match_percentage}
                  onChange={handleInputChange}
                  data-testid="price-match-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  The percentage guests save by booking directly (shown in comparison badge)
                </p>
              </div>
            </CardContent>
          </Card>

          <Button 
            type="submit" 
            className="bg-accent hover:bg-accent/90"
            disabled={loading}
            data-testid="save-settings-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
